import { useSelector } from 'react-redux'
import {
  BookkeeperAccountVisibilityTypes,
  OrganizationRoles,
  OrgIntegrations,
} from 'src/enums'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { selectIndustryOptions } from 'src/redux/selectors/data/config.selectors'

export interface OrganizationsListResponse {
  id: string
  name: string
  industry: string
  useCase: string
  owner: string
  explicit: boolean
  receiptRule?: OrganizationReceiptRulesResponse
  hasRecurringCards?: boolean
  currencies?: CurrencyResponse[]
  expenseApprover?: UserListResponse
  isOutOfPocketExpensesVisible: boolean
  mileageRate?: number
  integrations?: OrgIntegrations[]
  memberListVisible: boolean
  createdAt: string
  physicalClearingsEnabled?: boolean
}

export class OrganizationsListModel {
  public id: string
  public name: string
  public industry: string
  public useCase: string
  public owner: string
  public explicit: boolean
  public receiptRule?: OrganizationReceiptRulesResponse
  public hasRecurringCards?: boolean
  public currencies?: CurrencyResponse[]
  public expenseApprover?: UserListModel
  public isOutOfPocketExpensesVisible: boolean
  public mileageRate?: number
  public integrations?: OrgIntegrations[]
  public memberListVisible: boolean
  public createdAt: string
  public physicalClearingsEnabled?: boolean

  public constructor(data: OrganizationsListResponse) {
    this.id = data.id
    this.name = data.name
    this.industry = data.industry
    this.useCase = data.useCase
    this.owner = data.owner
    this.explicit = data.explicit
    this.receiptRule = data.receiptRule
    this.hasRecurringCards = data.hasRecurringCards
    this.currencies = data.currencies
    this.expenseApprover =
      data.expenseApprover !== undefined
        ? new UserListModel(data.expenseApprover)
        : undefined
    this.isOutOfPocketExpensesVisible = data.isOutOfPocketExpensesVisible
    this.mileageRate = data.mileageRate
    this.integrations = data.integrations
    this.memberListVisible = data.memberListVisible
    this.createdAt = data.createdAt
    this.physicalClearingsEnabled = data.physicalClearingsEnabled
  }

  public get getLocalizedIndustryString(): string {
    const supportIndustriesLocalized = useSelector(selectIndustryOptions)
    const localizedString = Object.values(
      supportIndustriesLocalized.filter(
        (industry) => industry.value === this.industry,
      ),
    ).map((industry) => (industry.details ? industry.details.text : ''))[0]
    return localizedString !== undefined ? localizedString : this.industry
  }

  public get mileageRateThousandths(): number | undefined {
    return this.mileageRate !== undefined ? this.mileageRate / 1000 : undefined
  }
}

export interface BookkeeperAccountVisibility {
  type: BookkeeperAccountVisibilityTypes
  ids?: string[]
}

export interface OrganizationInviteResponse {
  createdAt: string
  id: string
  invitee: UserListModel
  inviter: UserListModel
  organization: OrganizationsListResponse
  status: string
  role: OrganizationRoles
  accountVisibility?: BookkeeperAccountVisibility
}

export class OrganizationInviteModel {
  public createdAt: string
  public id: string
  public invitee: UserListModel
  public inviter: UserListModel
  public organization: OrganizationsListResponse
  public status: string
  public role: OrganizationRoles

  public constructor(data: OrganizationInviteResponse) {
    this.createdAt = data.createdAt
    this.id = data.id
    this.invitee = new UserListModel(data.invitee)
    this.inviter = new UserListModel(data.inviter)
    this.organization = data.organization
    this.status = data.status
    this.role = data.role
  }

  public get orgRoleDisplayTitle(): string {
    return this.role === OrganizationRoles.owner
      ? 'Owner'
      : this.role === OrganizationRoles.admin
        ? 'Admin'
        : this.role === OrganizationRoles.cardManager
          ? 'Card Manager'
          : this.role === OrganizationRoles.bookkeeper
            ? 'Bookkeeper'
            : this.role === OrganizationRoles.member
              ? 'Member'
              : this.role === OrganizationRoles.guest
                ? 'Guest'
                : ''
  }

  public get orgRoleDisplayTitlePlural(): string {
    return this.role === OrganizationRoles.owner
      ? 'Owners'
      : this.role === OrganizationRoles.admin
        ? 'Admins'
        : this.role === OrganizationRoles.cardManager
          ? 'Card Managers'
          : this.role === OrganizationRoles.bookkeeper
            ? 'Bookkeepers'
            : this.role === OrganizationRoles.member
              ? 'Members'
              : this.role === OrganizationRoles.guest
                ? 'Guests'
                : ''
  }

  public get isAdmin(): boolean {
    return this.role === OrganizationRoles.admin
  }

  public get isBookkeeper(): boolean {
    return this.role === OrganizationRoles.bookkeeper
  }

  public get isMember(): boolean {
    return this.role === OrganizationRoles.member
  }

  public get isCardManager(): boolean {
    return this.role === OrganizationRoles.cardManager
  }
}

export interface OrganizationReceiptRulesResponse {
  enabled?: boolean
  usdThresholdCents?: number
  cadThresholdCents?: number
  standardCardsEnabled?: boolean
  recurringCardsEnabled?: boolean
  physicalCardsEnabled?: boolean
  id: string
  effectiveAt?: string
}

export interface ManagerApprovalRulesResponse {
  id: string
  organizationId: string
  managerReviewEnabled: boolean
  managerReviewUsdThresholdCents?: number
  managerReviewCadThresholdCents?: number
  policyAssistantEnabled: boolean
}

export interface ExpensePolicyResponse {
  id: string
  organizationId: string
  contentType: string
  fileName: string
  urls: {
    original: string
    thumb: string
  }
  createdAt?: string
  updatedAt: string
}

export interface CurrencyResponse {
  inputField: keyof OrganizationReceiptRulesResponse
  currencyCode: string
  currencyNumCode: string
}

export interface ApprovalRuleResponse {
  approverId: string
  id: string
  type: ApprovalRuleType
  thresholds: {
    usdCents: number
    cadCents: number
  }
}

export interface CardApprovalBody {
  approverId: string
  type: ApprovalRuleType
  thresholds: {
    usdCents: number
    cadCents: number
  }
}

export enum ApprovalRuleType {
  Approver = 'approver',
}

export interface OrganizationFeatureFlagsResponse {
  organizationFeatureFlags: {
    organizationId: string
    guestRoleEnabled: boolean
    ownerAdminEnhancementsEnabled: boolean
    rolesMigrationAcksEnabled: boolean
    cardManagerRoleEnabled: boolean
    departmentsEnabled: boolean
  }
}

export class OrganizationFeatureFlagsModel {
  public organizationId: string
  public guestRoleEnabled: boolean
  public ownerAdminEnhancementsEnabled: boolean
  public rolesMigrationAcksEnabled: boolean
  public cardManagerRoleEnabled: boolean
  public departmentsEnabled: boolean

  public constructor(data: OrganizationFeatureFlagsResponse) {
    this.organizationId = data.organizationFeatureFlags.organizationId
    this.guestRoleEnabled = data.organizationFeatureFlags.guestRoleEnabled
    this.ownerAdminEnhancementsEnabled =
      data.organizationFeatureFlags.ownerAdminEnhancementsEnabled
    this.rolesMigrationAcksEnabled =
      data.organizationFeatureFlags.rolesMigrationAcksEnabled
    this.cardManagerRoleEnabled =
      data.organizationFeatureFlags.cardManagerRoleEnabled
    this.departmentsEnabled = data.organizationFeatureFlags.departmentsEnabled
  }
}

export type SaasTierType = 'STARTER' | 'PRO' | 'ENTERPRISE'

export const getSaasTierLabel = (tier: SaasTierType): string => {
  switch (tier) {
    case 'STARTER':
      return 'Starter'
    case 'PRO':
      return 'Pro'
    case 'ENTERPRISE':
      return 'Enterprise'
  }
}

export type OrganizationSaasTierResponse = {
  tier: SaasTierType
}

enum OrganizationSaasEntitlement {
  SAAS_ENABLED = 'SAAS_ENABLED',
  API_KEY = 'API_KEY',
  GUEST_ROLE = 'GUEST_ROLE',
  ORGANIZATION_TOTAL_SIZE = 'ORGANIZATION_TOTAL_SIZE',
  VIRTUAL_CARD_LIMIT = 'VIRTUAL_CARD_LIMIT',
  EXPENSE_CATEGORY_LIMIT = 'EXPENSE_CATEGORY_LIMIT',
  EXPENSE_CATEGORY_RULES = 'EXPENSE_CATEGORY_RULES',
  SEND_TO_BANK = 'SEND_TO_BANK',
  AUTOMATED_EXPENSE_REPORTS = 'AUTOMATED_EXPENSE_REPORTS',
  BUSINESS_RULES = 'BUSINESS_RULES',
  QUICKBOOKS_INTEGRATION = 'QUICKBOOKS_INTEGRATION',
  NETSUITE_INTEGRATION = 'NETSUITE_INTEGRATION',
  MANAGER_ASSIGNMENT = 'MANAGER_ASSIGNMENT',
}

export type OrganizationUsagesResponse = {
  features: Partial<
    Record<OrganizationSaasEntitlement, OrganizationUsageResponse>
  >
}

export type OrganizationUsageResponse = {
  entitlement: any
  usage: any
  withinLimit: boolean
}

export type SaasUrlResponse = {
  url: string | null
}

export type SubscriptionResponse = {
  subscription: SubscriptionView | null
}

export type SubscriptionView = {
  tier: Tier
  billingCycle: BillingCycle
  currency: Currency
  state: StatusView
}

export enum Tier {
  STARTER = 'STARTER',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
}

export enum BillingCycle {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
  UNKNOWN = 'UNKNOWN',
}

export enum Currency {
  USD = 'USD',
  UNKNOWN = 'UNKNOWN',
}

export enum SubscriptionStatus {
  FUTURE = 'FUTURE',
  IN_TRIAL = 'IN_TRIAL',
  ACTIVE = 'ACTIVE',
  NON_RENEWING = 'NON_RENEWING',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
  TRANSFERRED = 'TRANSFERRED',
  UNKNOWN = 'UNKNOWN',
}

export type StatusView =
  | { status: SubscriptionStatus.FUTURE; startDate: string }
  | { status: SubscriptionStatus.IN_TRIAL; trialEnd: string }
  | { status: SubscriptionStatus.ACTIVE; nextBillingAt: string }
  | { status: SubscriptionStatus.NON_RENEWING; cancelsAt: string }
  | { status: SubscriptionStatus.PAUSED; resumeDate: string | null }
  | { status: SubscriptionStatus.CANCELLED; cancelledAt: string }
  | { status: SubscriptionStatus.TRANSFERRED }
  | { status: SubscriptionStatus.UNKNOWN }

export enum ChurnSurveyReason {
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  MISSING_FEATURES = 'MISSING_FEATURES',
  SWITCHED_TO_COMPETITOR = 'SWITCHED_TO_COMPETITOR',
  NO_LONGER_NEEDED = 'NO_LONGER_NEEDED',
  TOO_DIFFICULT_TO_USE = 'TOO_DIFFICULT_TO_USE',
  POOR_SUPPORT = 'POOR_SUPPORT',
  JUST_TESTING = 'JUST_TESTING',
  OTHER = 'OTHER',
}

export const CHURN_REASONS_WITH_FREE_TEXT: ChurnSurveyReason[] = [
  ChurnSurveyReason.MISSING_FEATURES,
  ChurnSurveyReason.SWITCHED_TO_COMPETITOR,
]

export interface ChurnSurveyRequest {
  reason?: ChurnSurveyReason
  comment?: string
  additionalComment?: string
}

export interface ChurnSurveyReasonResponse {
  reason: ChurnSurveyReason
  freeTextEnabled: boolean
}

export interface ChurnSurveyReasonsResponse {
  reasons: ChurnSurveyReasonResponse[]
}
